
import { WorkSegment } from "../shared/timeline.js";
import { AppSpinner } from "./app-spinner.js";
import { AppToolCluster } from "./app-tool-cluster.js";
import { AutoMarkdown } from "./auto-markdown.js";
import { IconChevronDown } from "./icons/chevron-down.js";
import { IconChevronRight } from "./icons/chevron-right.js";

const styles = css`
  :host { display: block; min-width: 0; width: 100%; }
  .fold { border-bottom: 1px solid color-mix(in oklab, var(--border) 60%, transparent); padding: 4px 0 8px; }
  .fold-toggle { align-items: center; background: transparent; border: 0; border-radius: var(--radius-sm); color: var(--muted-foreground); cursor: pointer; display: inline-flex; font: inherit; font-size: 0.75rem; gap: 4px; line-height: 20px; padding: 1px 4px; text-align: left; }
  .fold-toggle:hover { color: var(--foreground); }
  .fold-toggle:focus-visible { box-shadow: 0 0 0 2px var(--ring-shadow); outline: none; }
  .fold-toggle app-spinner, .fold-toggle .chevron, .fold-toggle .chevron > * { height: 14px; width: 14px; }
  .chevron { align-items: center; display: inline-flex; justify-content: center; }
  .work-log { padding: 18px 4px 0; }
  .segment + .segment { margin-top: 14px; }
  .commentary { color: color-mix(in oklab, var(--foreground) 82%, transparent); font-size: 0.9375rem; line-height: 1.55; }
  .commentary + app-tool-cluster { margin-top: 10px; }
  @media (max-width: 767px) { .work-log { padding-left: 2px; padding-right: 2px; } }
`;

export class AppWorkGroup extends HTMLElement {
  static argonHydrate(element) {
    if (!(element instanceof AppWorkGroup)) return;
    if (element.isConnected && !element.#bindingsMounted) {
      element.#syncHydratedProps();
      element.connectedCallback();
    }
    if (element.#root) element.#wireEvents(element.#root);
  }
  static observedAttributes = ["label","data-label","segments","data-segments","running","data-running","startedat","data-started-at"];

  #label = "Worked";
  #segments = [];
  #running = false;
  #startedAt = 0;
  #expanded;
  #elapsed;
  #labelSet = false;
  #segmentsSet = false;
  #runningSet = false;
  #startedAtSet = false;
  #textStart0;
  #textEnd0;
  #textLast0;
  #textStart1;
  #textEnd1;
  #textLast1;
  #textStart2;
  #textEnd2;
  #textLast2;
  #attr0;
  #attrLast0;
  #hooksLive = false;
  #root;
  #bindingsMounted = false;
  #hydrating = false;
  #isCSR = false;

  get label() {
    return this.#label;
  }

  set label(value) {
    const raw = value ?? "Worked";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#label, next)) return;
    this.#label = next;
    this.#labelSet = true;
    this.#updateBindings('label');
  }

  get segments() {
    return this.#segments;
  }

  set segments(value) {
    const raw = value ?? [];
    const next = raw == null ? raw : typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (Object.is(this.#segments, next)) return;
    this.#segments = next;
    this.#segmentsSet = true;
    this.#updateBindings('segments');
  }

  get running() {
    return this.#running;
  }

  set running(value) {
    const raw = value ?? false;
    const next = raw == null ? raw : typeof raw === 'string' ? raw !== 'false' : Boolean(raw);
    if (Object.is(this.#running, next)) return;
    this.#running = next;
    this.#runningSet = true;
    this.#updateBindings('running');
  }

  get startedAt() {
    return this.#startedAt;
  }

  set startedAt(value) {
    const raw = value ?? 0;
    const next = raw == null ? raw : Number(raw);
    if (Object.is(this.#startedAt, next)) return;
    this.#startedAt = next;
    this.#startedAtSet = true;
    this.#updateBindings('startedAt');
  }

  #setExpanded(value) {
    if (Object.is(this.#expanded, value)) return;
    this.#expanded = value;
    this.#updateBindings('expanded');
  }

  #setElapsed(value) {
    if (Object.is(this.#elapsed, value)) return;
    this.#elapsed = value;
    this.#updateBindings('elapsed');
  }

  #syncHydratedProps() {
    if (this.#isCSR) return;
    if (this.hasAttribute('data-label')) this.label = this.dataset.label;
    if (this.hasAttribute('data-segments')) this.segments = this.dataset.segments;
    if (this.hasAttribute('data-running')) this.running = this.dataset.running;
    if (this.hasAttribute('data-started-at')) this.startedAt = this.dataset.startedAt;
  }

  constructor(props) {
    super();
    if (props !== undefined) {
      this.#isCSR = true;
      this.label = props.label;
      this.segments = props.segments;
      this.running = props.running;
      this.startedAt = props.startedAt;
      this.#initState();
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = this.#render();
      this.#mount(shadow, false);
    }
  }

  connectedCallback() {
    if (!this.#isCSR && !this.#bindingsMounted) {
      this.#syncHydratedProps();
    if (!this.#labelSet) this.label = this.dataset.label;
    if (!this.#segmentsSet) this.segments = this.dataset.segments;
    if (!this.#runningSet) this.running = this.dataset.running;
    if (!this.#startedAtSet) this.startedAt = this.dataset.startedAt;
    this.#initState();
    const shadow = this.shadowRoot;
    const root = shadow ?? this.attachShadow({ mode: 'open' });
    if (!shadow) root.innerHTML = this.#render();
    this.#mount(root, !!shadow);
    }
    if (this.#hooksLive) return;
    this.#hooksLive = true;
    this.#effect0();
  }

  disconnectedCallback() {
    this.#hooksLive = false;
    if (this.#effect0Cleanup) { this.#effect0Cleanup(); this.#effect0Cleanup = undefined; }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'label':
      case 'data-label':
        this.label = newValue;
        break;
      case 'segments':
      case 'data-segments':
        this.segments = newValue;
        break;
      case 'running':
      case 'data-running':
        this.running = newValue;
        break;
      case 'startedat':
      case 'data-started-at':
        this.startedAt = newValue;
        break;
    }
  }

  #initState() {
    this.#expanded = this.#running;
    this.#elapsed = 0;
  }

  #render() {
    const title = this.#running ? ((this.#elapsed > 0) ? `Working for ${this.#elapsed}s` : "Working…") : this.#label;
    return `<style>${this.#escText(styles)}</style><section class="fold"><button class="fold-toggle" type="button" aria-expanded="${this.#escAttr(this.#expanded ? "true" : "false")}" data-argon="2303" data-argon-bind="0"><span><!--argon-text:0-->${this.#escText(title)}<!--/argon-text:0--></span><!--argon-text:1-->${this.#running ? `<app-spinner>${""}</app-spinner>` : `<span class="chevron" aria-hidden="true">${this.#expanded ? `<icon-chevron-down>${""}</icon-chevron-down>` : `<icon-chevron-right>${""}</icon-chevron-right>`}</span>`}<!--/argon-text:1--></button><!--argon-text:2-->${this.#expanded ? `<div class="work-log">${this.#segments.map((segment) => `<div class="segment">${(segment.commentary !== "") ? `<auto-markdown class="commentary" data-text="${this.#attrEsc(segment.commentary)}" data-format="markdown">${""}<div class="rendered">${this.#escText(segment.commentary)}</div></auto-markdown>` : ""}${(segment.tools.length > 0) ? `<app-tool-cluster data-tools="${this.#attrEsc(segment.tools)}">${""}</app-tool-cluster>` : ""}</div>`).join("")}</div>` : ""}<!--/argon-text:2--></section>`;
  }



  #mount(root, hydrating) {
    this.#root = root;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
    let marker;
    while ((marker = walker.nextNode())) {
      switch (marker.nodeValue) {
        case 'argon-text:0':
          this.#textStart0 = marker;
          break;
        case '/argon-text:0':
          this.#textEnd0 = marker;
          break;
        case 'argon-text:1':
          this.#textStart1 = marker;
          break;
        case '/argon-text:1':
          this.#textEnd1 = marker;
          break;
        case 'argon-text:2':
          this.#textStart2 = marker;
          break;
        case '/argon-text:2':
          this.#textEnd2 = marker;
          break;
      }
    }
    this.#attr0 = root.querySelector('[data-argon-bind="0"]');
    this.#bindingsMounted = true;
    this.#hydrating = hydrating;
    this.#updateBindings();
    this.#hydrating = false;
    this.#wireEvents(root);
    if (!root.__argonEventObserver) {
      const observer = new window.MutationObserver(() => this.#wireEvents(root));
      observer.observe(root, { childList: true, subtree: true });
      Object.defineProperty(root, '__argonEventObserver', { value: observer });
    }
    this.setAttribute('hydrated', '');
    setTimeout(() => { if (this.#root === root) this.#wireEvents(root); }, 0);
  }

  #replaceRange(start, end, value) {
    if (!start || !end) return;
    const html = String(value);
    const sole = start.nextSibling;
    if (sole && sole !== end && sole.nextSibling === end && sole.nodeType === 3 && !/[<&]/.test(html)) {
      if (html.startsWith(sole.data)) sole.appendData(html.slice(sole.data.length));
      else sole.data = html;
      return;
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    if (sole && sole !== end && sole.nextSibling === end && template.content.childNodes.length === 1 && this.#patchElement(sole, template.content.firstChild)) return;
    let oldNode = start.nextSibling;
    let newNode = template.content.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode || oldNode === end) {
        end.before(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldNode.before(newNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode && oldNode !== end) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #patchElement(oldEl, newEl) {
    if (!oldEl || !newEl || oldEl.nodeType !== 1 || newEl.nodeType !== 1 || oldEl.localName !== newEl.localName) return false;
    for (const attr of [...oldEl.attributes]) {
      if (attr.name !== 'hydrated' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    }
    for (const attr of [...newEl.attributes]) {
      if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
    }
    this.#patchChildren(oldEl, newEl);
    this.#wireEvents(oldEl);
    return true;
  }

  #patchNode(oldNode, newNode) {
    if (!oldNode || !newNode) return false;
    if (oldNode.nodeType === 3 && newNode.nodeType === 3) {
      if (oldNode.data !== newNode.data) oldNode.data = newNode.data;
      return true;
    }
    if (oldNode.nodeType === 8 && newNode.nodeType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
      return true;
    }
    return this.#patchElement(oldNode, newNode);
  }

  #patchChildren(oldEl, newEl) {
    let oldNode = oldEl.firstChild;
    let newNode = newEl.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode) {
        oldEl.append(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldEl.insertBefore(newNode, oldNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }
  #attrRaw(v) {
    return typeof v === 'object' ? JSON.stringify(v) : String(v);
  }

  #attrEsc(v) {
    return this.#attrRaw(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
  }

  #escText(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  #escAttr(v) {
    return String(v).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }

  #effect0Cleanup;
  #effect0() {
    if (this.#effect0Cleanup) this.#effect0Cleanup();
    const cleanup = (() => {
    if (!this.#running)
        return;
    const update = () => { this.#setElapsed(this.#startedAt > 0 ? Math.max(0, Math.floor((Date.now() - this.#startedAt) / 1000)) : 0); };
    update();
    const timer = setInterval(update, 1000);
    return () => clearInterval(timer);
})();
    this.#effect0Cleanup = typeof cleanup === 'function' ? cleanup : undefined;
  }


  #eventTargets(scope, id) {
    const selector = `[data-argon="${id}"]`;
    const targets = [...scope.querySelectorAll(selector)];
    if (scope.nodeType === 1 && scope.matches(selector)) targets.unshift(scope);
    return targets;
  }

  #listen(el, id, event, handler) {
    const key = id + ':' + event;
    const root = this.#root;
    if (!root.__argonListeners) Object.defineProperty(root, '__argonListeners', { value: {} });
    const prev = root.__argonListeners[key];
    if (prev) root.removeEventListener(event, prev, true);
    const next = event => {
      const target = event.composedPath().find(node => node?.getAttribute?.('data-argon') === id);
      if (!target) return;
      const delegated = new Proxy(event, { get(source, prop) {
        if (prop === 'currentTarget') return target;
        const value = Reflect.get(source, prop, source);
        return typeof value === 'function' ? value.bind(source) : value;
      } });
      handler.call(target, delegated);
    };
    root.__argonListeners[key] = next;
    root.addEventListener(event, next, true);
  }

  #updateBindings(prop) {
    if (!this.#bindingsMounted) return;
    const title = (prop === undefined || prop === 'running' || prop === 'elapsed' || prop === 'label') ? (this.#running ? ((this.#elapsed > 0) ? `Working for ${this.#elapsed}s` : "Working…") : this.#label) : undefined;
    if (prop === undefined || prop === 'running' || prop === 'elapsed' || prop === 'label') {
      const __argonNext = `${this.#escText(title)}`;
      if (__argonNext !== this.#textLast0) {
        this.#textLast0 = __argonNext;
        this.#replaceRange(this.#textStart0, this.#textEnd0, __argonNext);
      }
    }
    if (prop === undefined || prop === 'running' || prop === 'expanded') {
      const __argonNext = `${this.#running ? `<app-spinner>${""}</app-spinner>` : `<span class="chevron" aria-hidden="true">${this.#expanded ? `<icon-chevron-down>${""}</icon-chevron-down>` : `<icon-chevron-right>${""}</icon-chevron-right>`}</span>`}`;
      if (__argonNext !== this.#textLast1) {
        this.#textLast1 = __argonNext;
        this.#replaceRange(this.#textStart1, this.#textEnd1, __argonNext);
      }
    }
    if (prop === undefined || prop === 'expanded' || prop === 'segments') {
      const __argonNext = `${this.#expanded ? `<div class="work-log">${this.#segments.map((segment) => `<div class="segment">${(segment.commentary !== "") ? `<auto-markdown class="commentary" data-text="${this.#attrEsc(segment.commentary)}" data-format="markdown">${""}<div class="rendered">${this.#escText(segment.commentary)}</div></auto-markdown>` : ""}${(segment.tools.length > 0) ? `<app-tool-cluster data-tools="${this.#attrEsc(segment.tools)}">${""}</app-tool-cluster>` : ""}</div>`).join("")}</div>` : ""}`;
      if (__argonNext !== this.#textLast2) {
        this.#textLast2 = __argonNext;
        this.#replaceRange(this.#textStart2, this.#textEnd2, __argonNext);
      }
    }
    if ((prop === undefined || prop === 'expanded') && this.#attr0) {
      const __argonNext = `${this.#expanded ? "true" : "false"}`;
      if (__argonNext !== this.#attrLast0) {
        this.#attrLast0 = __argonNext;
        this.#attr0.setAttribute('aria-expanded', __argonNext);
      }
    }
    if (this.#hooksLive && (prop === 'running' || prop === 'elapsed' || prop === 'startedAt')) this.#effect0();
  }

  #wireEvents(scope) {
    const root = this.#root;
    this.#eventTargets(scope, '2303').forEach(el => this.#listen(el, '2303', 'click', () => { this.#setExpanded(!this.#expanded); }));
    const roots = [scope];
    for (let index = 0; index < roots.length; index++) {
      const candidates = roots[index].nodeType === 1 ? [roots[index], ...roots[index].querySelectorAll('*')] : [...roots[index].querySelectorAll('*')];
      for (const element of candidates) {
        customElements.upgrade(element);
        const hydrate = () => {
          customElements.upgrade(element);
          const definition = customElements.get(element.localName);
          definition?.argonHydrate?.(element);
        };
        if (element.isConnected && customElements.get(element.localName)) hydrate();
        else if (element.localName.includes('-')) customElements.whenDefined(element.localName).then(hydrate);
        else queueMicrotask(hydrate);
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
  }
}
